/**
 * The sentiment ASPECT vocabulary, shared by both sides of the turn loop:
 * `chart-capability-contract` gates chart aspects, dash-meta's
 * `sentiment-data.types.ts` pins `SENTIMENT_TOPICS` against `QuerySentimentV3`
 * in both directions, and the probe suite names aspects from the labels.
 */

/**
 * Each aspect is a single score on the same provider as `overall`.
 * positive_review / negative_review are excluded (probed empty, 2026-07-07).
 */
export const SENTIMENT_ASPECT_METRICS = [
  'business_outlook',
  'career_growth',
  'compensation',
  'benefits',
  'healthcare',
  'retirement_pension',
  'childcare',
  'education_assistance',
  'wellness_programs',
  'work_life_balance',
  'diversity_and_inclusion',
  'leadership',
  'culture',
  'remote_work',
  'onboarding',
  'mentorship',
  'technology',
  'promotion_opportunities',
  'paid_time_off',
  'transparency',
  'efficiency_and_bureaucracy',
  'product_and_service_quality',
  'prestige',
  'coworkers',
  'mission_and_vision',
  'workplace_facilities',
  'training',
  'clients_and_customers',
  'company_growth',
  'schedule_flexibility',
  'job_security',
  'overtime',
  'family_support',
  'commission',
  'independence',
  'commute',
  'safety',
  'internal_communication',
  'office_politics',
  'harassment',
  'honesty',
  'ethics',
  'commitment',
  'compliance',
  'integrity',
] as const;

export type SentimentAspectMetric = (typeof SENTIMENT_ASPECT_METRICS)[number];

/** Metric-catalog labels; the `Record` type makes a missing one a compile error. */
export const SENTIMENT_ASPECT_LABELS: Record<SentimentAspectMetric, string> = {
  business_outlook: 'Business Outlook Sentiment',
  career_growth: 'Career Growth Sentiment',
  compensation: 'Compensation Sentiment',
  benefits: 'Benefits Sentiment',
  healthcare: 'Healthcare Sentiment',
  retirement_pension: 'Retirement & Pension Sentiment',
  childcare: 'Childcare Sentiment',
  education_assistance: 'Education Assistance Sentiment',
  wellness_programs: 'Wellness Programs Sentiment',
  work_life_balance: 'Work-Life Balance Sentiment',
  diversity_and_inclusion: 'Diversity & Inclusion Sentiment',
  leadership: 'Leadership Sentiment',
  culture: 'Culture Sentiment',
  remote_work: 'Remote Work Sentiment',
  onboarding: 'Onboarding Sentiment',
  mentorship: 'Mentorship Sentiment',
  technology: 'Technology Sentiment',
  promotion_opportunities: 'Promotion Opportunities Sentiment',
  paid_time_off: 'Paid Time Off Sentiment',
  transparency: 'Transparency Sentiment',
  efficiency_and_bureaucracy: 'Efficiency & Bureaucracy Sentiment',
  product_and_service_quality: 'Product & Service Quality Sentiment',
  prestige: 'Prestige Sentiment',
  coworkers: 'Coworkers Sentiment',
  mission_and_vision: 'Mission & Vision Sentiment',
  workplace_facilities: 'Workplace Facilities Sentiment',
  training: 'Training Sentiment',
  clients_and_customers: 'Clients & Customers Sentiment',
  company_growth: 'Company Growth Sentiment',
  schedule_flexibility: 'Schedule Flexibility Sentiment',
  job_security: 'Job Security Sentiment',
  overtime: 'Overtime Sentiment',
  family_support: 'Family Support Sentiment',
  commission: 'Commission Sentiment',
  independence: 'Independence Sentiment',
  commute: 'Commute Sentiment',
  safety: 'Safety Sentiment',
  internal_communication: 'Internal Communication Sentiment',
  office_politics: 'Office Politics Sentiment',
  harassment: 'Harassment Sentiment',
  honesty: 'Honesty Sentiment',
  ethics: 'Ethics Sentiment',
  commitment: 'Commitment Sentiment',
  compliance: 'Compliance Sentiment',
  integrity: 'Integrity Sentiment',
};
